import { ref, reactive, toRefs } from 'vue';
import axios from 'axios';
import useUtilities from '@/utilities/uiUtilities';

const application = reactive({
  applicationDetails: {
    applicationId: '',
    sessionId: '',
    sessionName: '',
    stayInSameUnit: false,
    graduatingThisYear: 'No',

    // Added by system and admin
    status: 'Submitted',
    deposit: false,
    agreement: false,
    agreementTermsAndConditions: false,
    handbookAgreement: false,
    unitId: '',
    waitlistStatus: '',
    waitlistReason: '',
    terminateContractReason: '',

    // Selected by Student
    dssAccessible: 'Waived',
    vehicleMake: '',
    vehicleModel: '',
    vehicleColor: '',
    vehicleState: '',
    vehicleLicense: '',
    apartmentPreference1: '',
    apartmentPreference2: '',
    liveWithSameGenders: 'Same',
    roommatesHaveSameDegree: true,
    roommatesHaveEmotionalSupportAnimal: true,
    roommatesGenders: [],

    // Student and roommates tendencies
    cleanlinessHabits: false,
    sharingHabits: false,
    socialHabits: false,
    roommatesClean: false,
    roommatesShare: false,
    roommatesSocial: false,
    shareEmail: false,

    // Preferred Roommates
    preferredRoommate1: '',
    preferredRoommate2: '',
    preferredRoommate3: '',
    preferredRoommate4: '',
    preferredRoommateDigipenEmail1: '',
    preferredRoommateDigipenEmail2: '',
    preferredRoommateDigipenEmail3: '',
    preferredRoommateDigipenEmail4: '',
    preferredRoommateEmail1: '',
    preferredRoommateEmail2: '',
    preferredRoommateEmail3: '',
    preferredRoommateEmail4: '',

    // Student info imported from Colleague
    colleagueId: '',
    userId: '',
    firstName: '',
    lastName: '',
    preferredFirstName: '',
    genderIdentity: '',
    genderIdentityOther: null,
    dateOfBirth: '',
    mobilePhone: '',
    permanentPhone: '',
    degree: '',
    digipenEmail: '',
    permanentEmail: '',
    permanentAddress: '',
    permanentAddress2: '',
    city: '',
    zip: '',
    country: '',
    state: '',
    citizenship: '',
    academicYear: '',

    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',
    emergencyContactEmail: '',

    permanentPhoneIsInternational: false,
    mobilePhoneIsInternational: false,
    emergencyContactPhoneIsInternational: false,

    livingLearningCommunityStatus: 'Waived',
    hasPetAllergies: false,
    isMorningPerson: false,
    isNightPerson: false,

    notes: '',

    latestApplicationManagementRequestType: '',
  },
  hasVehicle: ref(false),
});

const currentApplicationId = ref();
const studentsApplications = ref([]);

export default function useApplication(id) {
  const {
    notifyResult,
    convertFormattedBooleanColumnToBoolean,
  } = useUtilities();

  application.applicationDetails.applicationId = id;

  const createApplication = async () => {
    try {
      const response = await axios.post(`${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Applications`, application.applicationDetails);
      const { data } = response;
      application.applicationDetails = data;

      return response;
    } catch (error) {
      console.log(error);
      notifyResult(error.response.status, error.response);

      return error;
    }
  };

  // if the date of birth is default datetime then set to empty string
  const dateRemoveTime = (dateTime, setToEmpty = false) => {
    // if (dateTime === '0001-01-01T00:00:00') {
    //   return '';
    // }
    const newDate = new Date(dateTime);
    let result = newDate.toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    if (result === '01/01/1' && setToEmpty) {
      result = '';
    }

    return result;
  };

  const getApplication = async (applicationId) => {
    try {
      const url = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Applications/${applicationId}`;
      const response = await axios.get(url);
      const { data } = response;
      application.applicationDetails = data;
      application.applicationDetails.dateOfBirth = dateRemoveTime(
        application.applicationDetails.dateOfBirth,
      );
      application.applicationDetails.depositCompleted = dateRemoveTime(
        application.applicationDetails.depositCompleted,
      );
      application.applicationDetails.applicationCreated = dateRemoveTime(
        application.applicationDetails.applicationCreated,
      );
      application.applicationDetails.applicationAccepted = dateRemoveTime(
        application.applicationDetails.applicationAccepted,
      );
    } catch (error) {
      application.value = error;
      console.log(error);
      notifyResult(error.response.status, error.response);
    }
  };

  const renameBooleanValues = (data, columnToFilter) => {
    if (data.length !== 2 && ((data[0] && !data[1]) || (!data[0] && data[1]))) return data;
    const newData = data.map((item) => {
      let newItem = item;

      if (item === true) {
        newItem = 'Yes';
      } else if (item === false) {
        newItem = 'No';
      }

      if (columnToFilter === 'handbookAgreement' || columnToFilter === 'agreement') {
        if (item === true) {
          newItem = 'Received';
        } else if (item === false) {
          newItem = 'Pending';
        }
      }
      return newItem;
    });

    return newData;
  };

  const getColumnFilterValues = async (columnToFilter, sessionNames) => {
    try {
      const url = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Applications/GetColumnFilterValues`;
      const response = await axios.get(url,
        {
          params: {
            columnToFilter,
            sessionNames,
          },
        });
      const { data } = response;
      const result = renameBooleanValues(data.data, columnToFilter);

      return result;
    } catch (error) {
      console.log(error);
      notifyResult(error.response.status, error.response);
      return error.response;
    }
  };

  const getPreFilledApplication = async () => {
    // only pre fill the application if it is a new application
    // @todo this will change in the future to check if something has been retrieved using a flag
    // @todo also once we had saving of the application in the db, we need to make sure the next
    //       time one loads it does not retrieve for those loaded applications
    if (application.applicationDetails.colleagueId === '') {
      try {
        const url = `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Applications/prefilledApplication`;
        const response = await axios.get(url);
        const { data } = response;

        if (data) {
          data.dateOfBirth = dateRemoveTime(data.dateOfBirth, true);
          application.applicationDetails = data;
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  const getApplicationsByUserId = async (userId) => {
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Applications/ApplicationsByUserId`,
        {
          params: { userId },
        },
      );
      const { data } = response;
      studentsApplications.value = data.data;
      for (let i = 0; i < studentsApplications.value.length; i += 1) {
        studentsApplications.value[i].dateOfBirth = dateRemoveTime(
          studentsApplications.value[i].dateOfBirth,
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getApplicationsEmailIds = async (sessionId) => {
    try {
      const response = await axios.get(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Applications/getApplicationsEmailIds`,
        {
          params: { sessionId },
        },
      );
      const { data } = response;

      return data.data;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const saveApplicationProgress = async () => {
    try {
      // update progress
    } catch (error) {
      // log error
    }
  };

  const updateApplication = async (currentApplication) => {
    try {
      const result = await axios.put(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/applications/`,
        currentApplication,
      );

      return result;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const updateEmergencyContact = async (currentApplication) => {
    try {
      const result = await axios.put(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/applications/EmergencyContactInformation`,
        currentApplication,
      );

      return result;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const updateApplicationByAccounting = async (currentApplication) => {
    try {
      const result = await axios.put(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/applications/UpdateApplicationByAccounting`,
        currentApplication,
      );

      return result;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const updateApplicationByStudent = async (currentApplication) => {
    try {
      const result = await axios.put(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/applications/UpdateApplicationByStudent`,
        currentApplication,
      );

      return result;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const updateAgreements = async (currentApplication) => {
    try {
      const result = await axios.put(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/applications/updateAgreements`,
        currentApplication,
      );

      return result;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const updateVehicleInformation = async (currentApplication) => {
    try {
      const result = await axios.put(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/applications/VehicleInformation`,
        currentApplication,
      );

      return result;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const getApplicationsExport = async (
    page = 1, rowsPerPage, descending, sortBy, filter, sessionFilter, columnsSelected,
  ) => {
    try {
      const result = await axios.post(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/applications/applicationsExport`,
        {
          sessionFilter,
          columnsSelected,
        },
        {
          params: {
            page,
            rowsPerPage,
            descending,
            sortBy,
            filter,
          },
        },
      );

      return result;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const getApplicantEmails = async (searchFilter, sessionIds, columnFilterDict) => {
    try {
      const params = new URLSearchParams();
      params.append('searchFilter', searchFilter);
      for (let i = 0; i < sessionIds.length; i += 1) {
        params.append('sessionIds[]', sessionIds[i]);
      }
      for (const [key, value] of Object.entries(columnFilterDict)) { // eslint-disable-line
        for (let i = 0; i < value.length; i += 1) {
          params.append(`columnFilter[${key}]`, convertFormattedBooleanColumnToBoolean(key, value[i]));
        }
      }
      const response = await axios.get(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/Applications/getApplicantEmails`,
        {
          params,
        },
      );
      const { data } = response;

      return data.data;
    } catch (error) {
      console.log(error);
      return error.response;
    }
  };

  const submitApplicationManagementRequest = async (request) => {
    try {
      const result = await axios.post(
        `${process.env.VUE_APP_HOUSING_SERVICE_URL}/api/applications/managementRequest`,
        request,
      );

      return result;
    } catch (error) {
      console.log(error);
      notifyResult(error.response.status, error.response);
      return error.response;
    }
  };

  const resetApplication = async () => {
    currentApplicationId.value = null;
    application.applicationDetails.applicationId = '';
    application.applicationDetails.sessionId = '';
    application.applicationDetails.sessionName = '';
    application.applicationDetails.stayInSameUnit = false;
    application.applicationDetails.graduatingThisYear = 'No';

    // Added by system and admin
    application.applicationDetails.status = 'Submitted';
    application.applicationDetails.deposit = false;
    application.applicationDetails.agreement = false;
    application.applicationDetails.agreementTermsAndConditions = false;
    application.applicationDetails.handbookAgreement = false;
    application.applicationDetails.unitId = '';
    application.applicationDetails.waitlistStatus = '';
    application.applicationDetails.waitlistReason = '';
    application.applicationDetails.terminateContractReason = '';

    // Selected by Student
    application.applicationDetails.dssAccessible = 'Waived';
    application.applicationDetails.vehicleMake = '';
    application.applicationDetails.vehicleModel = '';
    application.applicationDetails.vehicleColor = '';
    application.applicationDetails.vehicleState = '';
    application.applicationDetails.vehicleLicense = '';
    application.applicationDetails.apartmentPreference1 = '';
    application.applicationDetails.apartmentPreference2 = '';
    application.applicationDetails.liveWithSameGenders = 'Same';
    application.applicationDetails.roommatesHaveSameDegree = true;
    application.applicationDetails.roommatesHaveEmotionalSupportAnimal = true;
    application.applicationDetails.roommatesGenders = [];

    // Student and roommates tendencies
    application.applicationDetails.cleanlinessHabits = false;
    application.applicationDetails.sharingHabits = false;
    application.applicationDetails.socialHabits = false;
    application.applicationDetails.roommatesClean = false;
    application.applicationDetails.roommatesShare = false;
    application.applicationDetails.roommatesSocial = false;
    application.applicationDetails.shareEmail = false;

    // Preferred Roommates
    application.applicationDetails.preferredRoommate1 = '';
    application.applicationDetails.preferredRoommate2 = '';
    application.applicationDetails.preferredRoommate3 = '';
    application.applicationDetails.preferredRoommate4 = '';
    application.applicationDetails.preferredRoommateDigipenEmail1 = '';
    application.applicationDetails.preferredRoommateDigipenEmail2 = '';
    application.applicationDetails.preferredRoommateDigipenEmail3 = '';
    application.applicationDetails.preferredRoommateDigipenEmail4 = '';
    application.applicationDetails.preferredRoommateEmail1 = '';
    application.applicationDetails.preferredRoommateEmail2 = '';
    application.applicationDetails.preferredRoommateEmail3 = '';
    application.applicationDetails.preferredRoommateEmail4 = '';

    // Student info imported from Colleague
    application.applicationDetails.colleagueId = '';
    application.applicationDetails.userId = '';
    application.applicationDetails.firstName = '';
    application.applicationDetails.lastName = '';
    application.applicationDetails.preferredFirstName = '';
    application.applicationDetails.genderIdentity = '';
    application.applicationDetails.genderIdentityOther = null;
    application.applicationDetails.dateOfBirth = '';
    application.applicationDetails.mobilePhone = '';
    application.applicationDetails.permanentPhone = '';
    application.applicationDetails.degree = '';
    application.applicationDetails.digipenEmail = '';
    application.applicationDetails.permanentEmail = '';
    application.applicationDetails.permanentAddress = '';
    application.applicationDetails.permanentAddress2 = '';
    application.applicationDetails.city = '';
    application.applicationDetails.zip = '';
    application.applicationDetails.country = '';
    application.applicationDetails.state = '';
    application.applicationDetails.citizenship = '';
    application.applicationDetails.academicYear = '';

    application.applicationDetails.emergencyContactName = '';
    application.applicationDetails.emergencyContactRelation = '';
    application.applicationDetails.emergencyContactPhone = '';
    application.applicationDetails.emergencyContactEmail = '';

    application.applicationDetails.emergencyContactIsInternational = false;
    application.applicationDetails.mobilePhoneIsInternational = false;
    application.applicationDetails.permanentPhoneIsInternational = false;

    application.applicationDetails.notes = '';
    application.hasVehicle = ref(false);
    application.applicationStatuses = [];

    application.applicationDetails.livingLearningCommunityStatus = 'Waived';
    application.applicationDetails.hasPetAllergies = false;
    application.applicationDetails.isMorningPerson = false;
    application.applicationDetails.isNightPerson = false;
  };

  return {
    ...toRefs(application),
    createApplication,
    currentApplicationId,
    // getApplications,
    getApplicantEmails,
    getApplication,
    getApplicationsByUserId,
    getApplicationsEmailIds,
    getColumnFilterValues,
    getPreFilledApplication,
    resetApplication,
    saveApplicationProgress,
    studentsApplications,
    updateAgreements,
    updateApplication,
    updateEmergencyContact,
    updateApplicationByAccounting,
    updateApplicationByStudent,
    updateVehicleInformation,
    getApplicationsExport,
    submitApplicationManagementRequest,
  };
}
